// Mermaid initialization util — RFC-0010 §4 (PD-0011 S2 render half).
//
// Kept separate from the component so the render config is a plain, testable
// module (and so the component file exports only a component — Fast Refresh).
//
// Security posture:
//   - `securityLevel: 'strict'` — disables click callbacks, node `href`
//     bindings, and arbitrary HTML in labels, and routes the rendered SVG
//     through Mermaid's built-in DOMPurify sanitization. Mermaid *source* is
//     untrusted repository content; strict mode is the control that makes
//     injecting its rendered output safe (AD-0001 §14.1).
//   - exact version pin (`mermaid: 11.4.1`, no `^`/`~` in package.json) so a
//     Mermaid upgrade is a deliberate PR, not silent `pnpm install` drift.
//
// CSP coordination (RFC-0010 §4 / N4, VERIFIED): mermaid@11.4.1 under strict
// mode contains no `eval(`/`new Function(` in any dist chunk, so it runs beneath
// RFC-0009's shipped `script-src 'self' '<theme-hash>'` WITHOUT `'unsafe-eval'`.
// Its only CSP dependency is the inline `<style>` it injects into the rendered
// SVG, covered by RFC-0009's existing `style-src 'unsafe-inline'`. RFC-0009's
// standing open question to drop `'unsafe-inline'` must therefore keep it for
// Mermaid.

import mermaid from 'mermaid';

/**
 * The exact Mermaid version this component is verified against. Must equal the
 * pinned range in apps/web/package.json (`mermaid: 11.4.1`, exact). Asserted by
 * the unit test against the installed package version.
 */
export const MERMAID_PINNED_VERSION = '11.4.1';

/** The security level Mermaid is initialized with — the PD-0011 S2 control. */
export const MERMAID_SECURITY_LEVEL = 'strict' as const;

let initialized = false;

/** Idempotently initialize Mermaid with the strict security posture. */
export function initMermaid(): void {
  if (initialized) return;
  mermaid.initialize({
    startOnLoad: false,
    securityLevel: MERMAID_SECURITY_LEVEL,
    theme: 'default',
    fontFamily: 'inherit',
  });
  initialized = true;
}
